import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql, navigate } from "gatsby";
import { useI18next } from "../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import { ItemList } from "schema-dts";

import * as styles from "./blog.module.scss";
import { IPostBrief, IPostCategory } from "../../models/post.model";
import { ITitleProps } from "../atoms/title";
import QueryEdgeNode from "../../models/query-edge-node.model";
import { IAuthor } from "../../models/author.model";
import { ISite } from "../../models/site.model";
import { IPageBase } from "../../models/page-base.model";
import getNodes from "../../utils/get-nodes";
import usePageMetadata from "../../hooks/use-page-metadata";
import createBlogListing from "../../utils/structured-data/create-blog-listing";
import useUser from "../../apps/auth/hooks/use-user";
import { getAbsolutePath } from "../../apps/utility/routes.utility";

import MainLayout from "../../layouts/main-layout";
import Link from "../atoms/link";
import Section from "../molecules/section";
import SectionWrapper from "../molecules/section-wrapper";
import BlogList, { IBlogListProps } from "../organisms/blog/blog-list";
import logoFull from "../../assets/images/logo_full.png";

export interface IBlogProps extends IPageBase {
    readonly data: {
        posts: QueryEdgeNode<IPostBrief>;
        authors: QueryEdgeNode<IAuthor>;
        categories: QueryEdgeNode<IPostCategory>;
        site: ISite;
    };
}

const navItems = [
    { label: "O Mnie", to: "/o-nas/" },
    { label: "Blog", to: "/blog/" },
    { label: "Kontakt", to: "/kontakt/" },
];

const BLOG_COLUMNS_PER_ROW = 4;
const BLOG_INITIAL_ROWS = 4;
const BLOG_ROWS_PER_LOAD = 1;
const BLOG_INITIAL_POSTS = BLOG_INITIAL_ROWS * BLOG_COLUMNS_PER_ROW;
const BLOG_POSTS_PER_LOAD = BLOG_ROWS_PER_LOAD * BLOG_COLUMNS_PER_ROW;

const Blog: React.FC<IBlogProps> = ({ data, pageContext }) => {
    const { categoryId } = pageContext;
    const { t } = useI18next();
    const { pageTitle } = usePageMetadata(pageContext);
    const [structuredData, setStructuredData] = useState<ItemList>();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [areFiltersOpen, setAreFiltersOpen] = useState(false);
    const [visiblePostsCount, setVisiblePostsCount] = useState(BLOG_INITIAL_POSTS);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const { isLoggedIn } = useUser();
    const accountLinkHref = isLoggedIn ? getAbsolutePath("ACCOUNT_DASHBOARD") : "/auth/";
    const accountLinkLabel = isLoggedIn ? "Twoje konto" : "Zaloguj się";

    const title: ITitleProps = {
        subtitle: t("common.blog"),
        children: t("posts-section.title"),
        align: "center",
    };

    const allPosts = useMemo(() => getNodes(data.posts), [data.posts]);
    const posts = useMemo(() => categoryId
        ? allPosts.filter(post => (
            post.categories || []
        ).some(category => category.categoryId === categoryId))
        : allPosts, [allPosts, categoryId]);
    const visiblePosts = posts.slice(0, visiblePostsCount);
    const hasMorePosts = visiblePostsCount < posts.length;
    const authors = getNodes(data.authors);
    const categories = getNodes(data.categories);

    useEffect(() => {
        setVisiblePostsCount(BLOG_INITIAL_POSTS);
    }, [categoryId]);

    useEffect(() => {
        if (!hasMorePosts || !loadMoreRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0]?.isIntersecting) {
                    setVisiblePostsCount(count => Math.min(
                        count + BLOG_POSTS_PER_LOAD,
                        posts.length
                    ));
                }
            },
            { rootMargin: "480px 0px" }
        );

        observer.observe(loadMoreRef.current);

        return () => observer.disconnect();
    }, [hasMorePosts, posts.length, visiblePostsCount]);

    const handleCreatePostItems: IBlogListProps["onCreatePostItems"] = useCallback((postItems) => {
        setStructuredData(createBlogListing(postItems, data.site.siteMetadata));
    }, []);

    return (
        <MainLayout
            HeaderComponent={null}
            footer={false}
            className={styles.blog}
            SEOProps={{
                title: pageTitle,
                structuredData,
            }}
        >
            <nav className={styles.blogNav} aria-label="Główna nawigacja">
                <Link to="/" className={styles.brand}>
                    <img
                        src={logoFull}
                        alt="Fashion Fairy Virtual Stylist"
                        className={styles.brandLogo}
                    />
                </Link>
                <div className={styles.navLinks}>
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>
                            {item.label}
                        </Link>
                    ))}
                </div>
                <Link to={accountLinkHref} className={styles.loginButton} noPrefetch>
                    {accountLinkLabel}
                </Link>
                <button
                    className={styles.menuToggle}
                    type="button"
                    aria-label={isMenuOpen ? "Zamknij menu" : "Otwórz menu"}
                    aria-expanded={isMenuOpen}
                    aria-controls="blog-mobile-menu"
                    onClick={() => {
                        setAreFiltersOpen(false);
                        setIsMenuOpen(isOpen => !isOpen);
                    }}
                >
                    <span />
                    <span />
                    <span />
                </button>
                <div
                    id="blog-mobile-menu"
                    className={`${styles.mobileMenu} ${
                        isMenuOpen ? styles.mobileMenuOpen : ""
                    }`}
                >
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to}>
                            {item.label}
                        </Link>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            setIsMenuOpen(false);
                            navigate(accountLinkHref);
                        }}
                    >
                        {accountLinkLabel}
                    </button>
                </div>
            </nav>

            <SectionWrapper className={styles.titleSectionWrapper}>
                <Section column="regular" title={title} className={styles.titleSection}>
                    <div className={styles.filtersSection}>
                        <button
                            className={styles.filtersToggle}
                            type="button"
                            aria-expanded={areFiltersOpen}
                            aria-controls="blog-category-filters"
                            onClick={() => {
                                setIsMenuOpen(false);
                                setAreFiltersOpen(isOpen => !isOpen);
                            }}
                        >
                            <span>Tematy</span>
                            <i aria-hidden="true" />
                        </button>
                        <div
                            id="blog-category-filters"
                            className={`${styles.filtersPanel} ${
                                areFiltersOpen ? styles.filtersPanelOpen : ""
                            }`}
                        >
                            <ul className={styles.topicList}>
                                <li>
                                    <Link
                                        to="/blog/"
                                        className={!categoryId
                                            ? styles.topicLinkActive
                                            : styles.topicLink}
                                    >
                                        Pokaż wszystkie
                                    </Link>
                                </li>
                                {categories.map(category => (
                                    <li key={category.categoryId}>
                                        <Link
                                            to={`/blog/${category.slug}/`}
                                            className={
                                                category.categoryId === categoryId
                                                    ? styles.topicLinkActive
                                                    : styles.topicLink
                                            }
                                        >
                                            {category.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Section>
            </SectionWrapper>
            <Section column="full" className={styles.blogGridSection}>
                <BlogList
                    posts={visiblePosts}
                    authors={authors}
                    listClassName={styles.blogListing}
                    itemClassName={styles.blogPostCard}
                    paginationKeys={[]}
                    onCreatePostItems={handleCreatePostItems}
                    categoryId={categoryId}
                />
                {hasMorePosts && (
                    <div
                        ref={loadMoreRef}
                        className={styles.blogLoader}
                        aria-label="Ładowanie kolejnych artykułów"
                    >
                        <span />
                        <span />
                        <span />
                    </div>
                )}
            </Section>
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }

        posts: allPost(sort: { fields: publishedAt, order: DESC }) {
            edges {
                node {
                    ...postBriefFields
                }
            }
        }

        authors: allAuthor {
            edges {
                node {
                    ...authorShortFields
                }
            }
        }

        categories: allPostCategory(filter: { postsIds: { nin: [] } }) {
            edges {
                node {
                    ...postCategoryFields
                }
            }
        }

        site {
            ...siteFields
        }
    }
`;

export default Blog;
